import type { QueryClient } from '@tanstack/react-query'
import { getApi } from '@/stores/api'
import { clearPermissions, setPermissions } from '@/stores/permissions'
import { setSessionToken, setTenant, type TenantInfo } from '@/stores/session'

type SwitchResult = { tenant: TenantInfo; sessionToken: string }

/**
 * Single source of truth for the driver-portal tenant switch flow. Hits
 * /portal-auth/driver/switch-tenant, updates session + tenant atoms, prunes
 * the React Query cache so old-tenant data can't leak, and warms /me +
 * driver-onboarding under the new session before returning. Prefetching the
 * dashboard query here is what keeps the destination route from briefly
 * flashing its loading state after navigation.
 *
 * Stays UI-agnostic on purpose — the React hook layers in pending state and
 * navigation, and the route's beforeLoad calls it directly during a
 * cross-tenant deep link without needing any of that.
 */
export async function performTenantSwitch(
  tenantId: string,
  queryClient: QueryClient,
): Promise<SwitchResult> {
  const api = getApi()
  const res = await api['portal-auth'].driver['switch-tenant'].$post({
    json: { tenantId },
  })
  const data = await res.json()

  setSessionToken(data.sessionToken)
  setTenant(data.tenant)
  clearPermissions()

  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' })

  const [meData] = await Promise.all([
    queryClient.fetchQuery({
      queryKey: ['me'],
      queryFn: async () => {
        const me = await getApi().users.me.$post()
        return me.json()
      },
      staleTime: 0,
    }),
    queryClient.prefetchQuery({
      queryKey: ['driver-onboarding'],
      queryFn: async () => {
        const onboarding = await getApi()['driver-onboarding'].onboarding.me.$get()
        return onboarding.json()
      },
    }),
  ])

  if (meData?.permissions) {
    setPermissions(meData.permissions)
  }

  return { tenant: data.tenant, sessionToken: data.sessionToken }
}
